import { z } from 'zod';
import { accessLevelSchema, approvesAiSchema, dataScopeSchema, MODULES, type ModuleKey, roleSchema } from '../enums';
import { emailSchema, idSchema } from './common';

/**
 * Kontrakt logowania i sesji: docs/API_AUTH.md.
 * REST: /api/auth/*  ·  tRPC: identity.*, team.listMembers
 */

// ── Stałe ───────────────────────────────────────────────────────────────────
export const SESSION_COOKIE = 'fos_session';
export const CSRF_COOKIE = 'fos_csrf';
export const CSRF_HEADER = 'x-fos-csrf';
/** Sesja z „Zapamiętaj mnie”: 30 dni. */
export const SESSION_TTL_REMEMBER_SEC = 30 * 24 * 3600;
/** Sesja bez „Zapamiętaj mnie”: 12 h (ciasteczko sesyjne przeglądarki, serwer wygasza). */
export const SESSION_TTL_SHORT_SEC = 12 * 3600;
/** Ważność step-up („tryb sudo”). */
export const STEP_UP_TTL_SEC = 10 * 60;
/**
 * Podgląd i kopia sekretu z sejfu: potwierdzenie KODEM Z MAILA nie starsze niż 5 min (Fala 7; wcześniej hasło ≤ 60 s).
 * Alias `VAULT_SECOND_FACTOR_MAX_AGE_SEC` (niżej).
 */
export const VAULT_STEP_UP_MAX_AGE_SEC = 5 * 60;
/** Link resetu hasła: 30 min, jednorazowy. */
export const PASSWORD_RESET_TTL_SEC = 30 * 60;
/** Blokada konta: po 5 nieudanych próbach 15 min (rosnąco). */
export const LOGIN_MAX_FAILED_ATTEMPTS = 5;
export const LOGIN_LOCK_BASE_SEC = 15 * 60;
/** Limit na IP: 10 prób / 5 min. */
export const LOGIN_IP_LIMIT = { max: 10, windowSec: 5 * 60 } as const;
/** Reset hasła: 3 / 15 min na adres, 10 / 15 min na IP. */
export const FORGOT_EMAIL_LIMIT = { max: 3, windowSec: 15 * 60 } as const;
export const FORGOT_IP_LIMIT = { max: 10, windowSec: 15 * 60 } as const;
/** Polityka haseł (sprawdza `validatePasswordPolicy` w @fos/core/identity/password). */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
/** Blokada konta rośnie (15 min, 30 min, 1 h, …) do najwyżej 24 h. */
export const LOGIN_LOCK_MAX_SEC = 24 * 3600;
/** `sessions.last_seen_at` aktualizowane najwyżej co 5 min. */
export const SESSION_TOUCH_INTERVAL_SEC = 5 * 60;
/**
 * Ciasteczko rozpoznawania urządzenia (HttpOnly, Path=/api/auth, 400 dni): podpisany HMAC identyfikator
 * przeglądarki powiązany z kontem. Służy tylko do maila „Nowe logowanie do Fravity OS”.
 */
export const DEVICE_COOKIE = 'fos_device';
export const DEVICE_COOKIE_MAX_AGE_SEC = 400 * 24 * 3600;

// ── Drugi składnik: kod z maila (Fala 7, docs/API_AUTH.md „2FA kodem z maila”) ──
/** Kod: 6 cyfr, ważny 10 min, najwyżej 5 prób (potem „spalony”). */
export const SECOND_FACTOR_CODE_LENGTH = 6;
export const SECOND_FACTOR_CODE_TTL_SEC = 10 * 60;
export const SECOND_FACTOR_MAX_ATTEMPTS = 5;
/** 3 spalone kody (= 15 złych kodów od ostatniego udanego potwierdzenia, okno 24 h) = blokada konta jak przy haśle. */
export const SECOND_FACTOR_BURNED_CODES_LOCK = 3;
/** Sesja czekająca na kod (stage `second_factor_required`) wygasa po 30 min. */
export const SECOND_FACTOR_PENDING_TTL_SEC = 30 * 60;
/** „Wyślij ponownie”: najwcześniej 30 s po poprzednim kodzie (odliczanie w UI). */
export const SECOND_FACTOR_RESEND_COOLDOWN_SEC = 30;
/** Ponowne wysłanie kodu logowania: 3 / 15 min na konto, 10 / 15 min na IP (liczą się kody niepotwierdzone). */
export const SECOND_FACTOR_RESEND_ACCOUNT_LIMIT = { max: 3, windowSec: 15 * 60 } as const;
export const SECOND_FACTOR_RESEND_IP_LIMIT = { max: 10, windowSec: 15 * 60 } as const;
/** Kody logowania (po poprawnym haśle): najwyżej 10 niepotwierdzonych / 15 min na konto (ochrona skrzynki). */
export const SECOND_FACTOR_LOGIN_CODE_LIMIT = { max: 10, windowSec: 15 * 60 } as const;
/** Kody potwierdzenia w sesji (sejf, włączenie 2FA): 5 / 15 min na konto, 10 / 15 min na IP. */
export const SECOND_FACTOR_STEP_UP_ACCOUNT_LIMIT = { max: 5, windowSec: 15 * 60 } as const;
export const SECOND_FACTOR_STEP_UP_IP_LIMIT = { max: 10, windowSec: 15 * 60 } as const;
/** Zaufane urządzenie omija kod przez 30 dni od potwierdzenia (bez przedłużania przy użyciu). */
export const TRUSTED_DEVICE_TTL_SEC = 30 * 24 * 3600;
/** Podgląd i kopia sekretu z sejfu: kod z maila nie starszy niż 5 min (`requireStepUp(ctx, { kind: 'second_factor' })`). */
export const VAULT_SECOND_FACTOR_MAX_AGE_SEC = VAULT_STEP_UP_MAX_AGE_SEC;
/** Przełącznik dobrowolnego 2FA w Ustawieniach: świeży kod (≤ 5 min). */
export const SECOND_FACTOR_SETTINGS_MAX_AGE_SEC = 5 * 60;

/** Rodzaj potwierdzenia w `STEP_UP_REQUIRED` (`error.details.kind`): hasło (10 min) albo kod z maila. */
export const STEP_UP_KINDS = ['password', 'second_factor'] as const;
export type StepUpKind = (typeof STEP_UP_KINDS)[number];

/** Stan sesji: `second_factor_required` = hasło poprawne, czeka na kod (bez dostępu do tRPC i REST poza /api/auth/*). */
export const AUTH_STAGES = ['active', 'second_factor_required'] as const;

/** `auth_tokens.purpose` kodów `email_2fa`: logowanie, ponowne wysłanie przy logowaniu, potwierdzenie w sesji. */
export const AUTH_TOKEN_PURPOSES = ['login', 'resend', 'step_up'] as const;
export type AuthTokenPurpose = (typeof AUTH_TOKEN_PURPOSES)[number];

/** Dlaczego konto wymaga drugiego składnika (liczone przy każdym logowaniu). */
export const SECOND_FACTOR_REASONS = ['role', 'vault', 'billing', 'voluntary'] as const;
export type SecondFactorReason = (typeof SECOND_FACTOR_REASONS)[number];
export const SECOND_FACTOR_REASON_LABELS: Record<SecondFactorReason, string> = {
  role: 'rola Właściciel, Admin albo Manager',
  vault: 'dostęp do sejfu',
  billing: 'dostęp do rozliczeń',
  voluntary: 'włączone przez Ciebie',
};

/**
 * Ścieżki SPA używane w linkach z maili. apps/web musi je obsłużyć (pilnuje tego test
 * apps/web/src/app/routes.test.tsx). Lista urządzeń jest w Ustawieniach → Bezpieczeństwo.
 */
export const AUTH_WEB_PATHS = {
  /** `?token=...` */
  resetPassword: '/reset-hasla',
  forgotPassword: '/nie-pamietam-hasla',
  login: '/login',
  devices: '/ustawienia/bezpieczenstwo',
} as const;

export const AUTH_MESSAGES = {
  invalidCredentials: 'Nieprawidłowy email lub hasło',
  tooManyAttempts: 'Zbyt wiele prób. Spróbuj ponownie za 15 min.',
  invalidResetToken: 'Link do resetu hasła jest nieprawidłowy albo wygasł.',
  passwordChanged: 'Hasło zostało zmienione',
  stepUpRequired: 'Potwierdź hasłem, aby kontynuować.',
  csrf: 'Nieprawidłowy token bezpieczeństwa. Odśwież stronę.',
  noMembership: 'Nie masz dostępu do tego workspace’u.',
  wrongPassword: 'Nieprawidłowe hasło',
  wrongCurrentPassword: 'Obecne hasło jest nieprawidłowe',
  samePassword: 'Nowe hasło musi się różnić od obecnego.',
  sessionNotFound: 'Nie znaleziono tej sesji.',
  secondFactorStepUpRequired: 'Potwierdź kodem z maila, aby kontynuować.',
  secondFactorSessionRequired: 'Twoje uprawnienia wymagają potwierdzenia kodem z maila.',
  invalidCode: 'Nieprawidłowy kod',
  codeFormat: 'Kod ma 6 cyfr',
  codeExpired: 'Kod wygasł. Wyślij nowy kod.',
  codeBurned: 'Kod zablokowany po 5 błędnych próbach. Wyślij nowy kod.',
  loginExpired: 'Logowanie wygasło. Zaloguj się ponownie.',
  trustedDeviceNotFound: 'Nie znaleziono tego urządzenia.',
  secondFactorRequiredByRole: 'Twoje konto wymaga kodu z maila. Nie można go wyłączyć.',
} as const;

/** „Wyślij ponownie za N s” / „za N min” (429 przy ponownym wysłaniu kodu). */
export function resendAvailableMessage(retryAfterSec: number): string {
  const sec = Math.max(1, Math.ceil(retryAfterSec));
  return sec < 120 ? `Nowy kod możesz wysłać za ${sec} s.` : `Nowy kod możesz wysłać za ${Math.ceil(sec / 60)} min.`;
}

/** „Zbyt wiele prób. Spróbuj ponownie za N min.” (N zaokrąglone w górę, co najmniej 1). */
export function tooManyAttemptsMessage(retryAfterSec: number): string {
  const minutes = Math.max(1, Math.ceil(retryAfterSec / 60));
  return `Zbyt wiele prób. Spróbuj ponownie za ${minutes} min.`;
}

/** Hasło w żądaniu. Polityka (12–128 znaków, popularne hasła) jest sprawdzana w usłudze, nie tutaj. */
const passwordInput = z.string().min(1, { message: 'Podaj hasło' }).max(1024, { message: 'Hasło jest za długie' });

// ── REST: /api/auth/* ───────────────────────────────────────────────────────
export const loginInput = z.object({
  email: emailSchema,
  password: passwordInput,
  remember: z.boolean().default(true),
});
export type LoginInput = z.infer<typeof loginInput>;

/** `active` albo `second_factor_required` (hasło poprawne, czeka na kod z maila). */
export const authStageSchema = z.enum(AUTH_STAGES);
export type AuthStage = z.infer<typeof authStageSchema>;

/**
 * Wyzwanie drugiego składnika (POST /api/auth/login i GET /api/auth/session przy `second_factor_required`).
 * `email` jest częściowo ukryty („s•••@fravity.local”); kodu nigdy nie ma w odpowiedzi.
 */
export const secondFactorChallengeSchema = z.object({
  email: z.string(),
  codeExpiresAt: z.string().datetime(),
  /** najwcześniejsze „Wyślij ponownie” (odliczanie w UI) */
  resendAvailableAt: z.string().datetime(),
  /** domyślny stan „Zaufaj temu urządzeniu przez 30 dni” (= „Zapamiętaj mnie”) */
  trustDeviceDefault: z.boolean(),
});
export type SecondFactorChallenge = z.infer<typeof secondFactorChallengeSchema>;

export const loginOutput = z.object({
  stage: authStageSchema,
  /** tylko przy `second_factor_required` */
  secondFactor: secondFactorChallengeSchema.optional(),
});
export type LoginOutput = z.infer<typeof loginOutput>;

/** Kod z maila: 6 cyfr (spacje i myślniki z wklejenia są pomijane). */
export const secondFactorCodeSchema = z
  .string()
  .max(32, { message: 'Kod ma 6 cyfr' })
  .transform((v) => v.replace(/[\s-]/g, ''))
  .pipe(z.string().regex(/^\d{6}$/, { message: 'Kod ma 6 cyfr' }));

/** POST /api/auth/second-factor/verify: sukces = `{ stage: 'active' }`, nowy token sesji. */
export const secondFactorVerifyInput = z.object({
  code: secondFactorCodeSchema,
  trustDevice: z.boolean().default(false),
});
export type SecondFactorVerifyInput = z.input<typeof secondFactorVerifyInput>;

/** POST /api/auth/second-factor/resend i POST /api/auth/step-up/code (wysłanie kodu). */
export const secondFactorSendOutput = z.object({
  ok: z.literal(true),
  email: z.string(),
  codeExpiresAt: z.string().datetime(),
  resendAvailableAt: z.string().datetime(),
});
export type SecondFactorSendOutput = z.infer<typeof secondFactorSendOutput>;

/** POST /api/auth/step-up/code/verify: potwierdzenie kodem w zalogowanej sesji (sejf, przełącznik 2FA). */
export const stepUpCodeInput = z.object({ code: secondFactorCodeSchema });
export type StepUpCodeInput = z.input<typeof stepUpCodeInput>;

export const forgotPasswordInput = z.object({ email: emailSchema });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordInput>;

export const resetPasswordInput = z.object({
  token: z.string().trim().min(16).max(256),
  password: passwordInput,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInput>;

export const stepUpInput = z.object({ password: passwordInput });
export type StepUpInput = z.infer<typeof stepUpInput>;

export const stepUpOutput = z.object({ ok: z.literal(true), validUntil: z.string().datetime() });
export type StepUpOutput = z.infer<typeof stepUpOutput>;

export const switchWorkspaceInput = z.object({ workspaceId: idSchema });
export type SwitchWorkspaceInput = z.infer<typeof switchWorkspaceInput>;

export const acceptInvitationInput = z.object({
  token: z.string().trim().min(16).max(256),
  name: z.string().trim().min(2, { message: 'Podaj imię i nazwisko' }).max(120),
  password: passwordInput,
});
export type AcceptInvitationInput = z.infer<typeof acceptInvitationInput>;

// ── GET /api/auth/session i identity.me ─────────────────────────────────────
export const sessionUserSchema = z.object({
  id: idSchema,
  email: z.string(),
  name: z.string(),
  initials: z.string(),
  avatarColor: z.string(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const sessionWorkspaceSchema = z.object({ id: idSchema, name: z.string(), slug: z.string() });
export type SessionWorkspace = z.infer<typeof sessionWorkspaceSchema>;

export const sessionMembershipSchema = z.object({ id: idSchema, role: roleSchema });
export type SessionMembership = z.infer<typeof sessionMembershipSchema>;

const moduleLevelsSchema = z.object(
  Object.fromEntries(MODULES.map((m) => [m, accessLevelSchema])) as Record<ModuleKey, typeof accessLevelSchema>,
);

export const sessionAccessSchema = z.object({
  modules: moduleLevelsSchema,
  flags: z.object({
    seesRates: z.boolean(),
    sendsEmails: z.boolean(),
    approvesAi: approvesAiSchema,
    preparesBilling: z.boolean(),
    issuesInvoices: z.boolean(),
    deploysProduction: z.boolean(),
    exportsData: z.boolean(),
    vaultAccess: z.boolean(),
    manageMembers: z.boolean(),
    aiMonthlyLimitPln: z.string().nullable(),
  }),
  dataScope: dataScopeSchema,
});
export type SessionAccess = z.infer<typeof sessionAccessSchema>;

export const sessionResponse = z.discriminatedUnion('authenticated', [
  z.object({ authenticated: z.literal(false) }),
  z.object({
    authenticated: z.literal(true),
    stage: authStageSchema,
    user: sessionUserSchema,
    workspace: sessionWorkspaceSchema.nullable(),
    membership: sessionMembershipSchema.nullable(),
    access: sessionAccessSchema.nullable(),
    /** tylko przy `second_factor_required` (wtedy workspace, membership i access = null) */
    secondFactor: secondFactorChallengeSchema.optional(),
  }),
]);
export type SessionResponse = z.infer<typeof sessionResponse>;

// ── tRPC identity.* ─────────────────────────────────────────────────────────
export const sessionListItemSchema = z.object({
  id: idSchema,
  deviceLabel: z.string(),
  ip: z.string().nullable(),
  createdAt: z.date(),
  lastSeenAt: z.date(),
  current: z.boolean(),
});
export type SessionListItem = z.infer<typeof sessionListItemSchema>;

export const revokeSessionInput = z.object({ id: idSchema });
export type RevokeSessionInput = z.infer<typeof revokeSessionInput>;

/** identity.revokeOtherSessions → liczba wylogowanych urządzeń. */
export const revokeOtherSessionsOutput = z.object({ ok: z.literal(true), revoked: z.number().int().min(0) });
export type RevokeOtherSessionsOutput = z.infer<typeof revokeOtherSessionsOutput>;

export const changePasswordInput = z.object({
  currentPassword: passwordInput,
  newPassword: passwordInput,
});
export type ChangePasswordInput = z.infer<typeof changePasswordInput>;

export const myWorkspaceItemSchema = z.object({
  id: idSchema,
  name: z.string(),
  slug: z.string(),
  role: roleSchema,
  /** główny workspace osoby (otwiera się po zalogowaniu; DECISIONS 2026-10-08 „Główny workspace”) */
  primary: z.boolean().optional(),
  /** członkostwo z szablonu „Partner (wspólne projekty)” — w menu workspace'ów etykieta „Partner” zamiast roli */
  partner: z.boolean().optional(),
});
export type MyWorkspaceItem = z.infer<typeof myWorkspaceItemSchema>;

// ── tRPC identity.* — drugi składnik (Ustawienia → Bezpieczeństwo) ─────────
/** Zaufane urządzenie (bez skrótu ciasteczka). `current` = ta przeglądarka. */
export const trustedDeviceItemSchema = z.object({
  id: idSchema,
  label: z.string(),
  createdAt: z.date(),
  lastUsedAt: z.date().nullable(),
  expiresAt: z.date(),
  current: z.boolean(),
});
export type TrustedDeviceItem = z.infer<typeof trustedDeviceItemSchema>;

/**
 * identity.secondFactorStatus: `required` = wymagane przez rolę lub uprawnienia (bez wyłączenia),
 * `enabled` = włączone dobrowolnie, `disabled` = wyłączone. `sessionVerified` = ta sesja przeszła kod
 * (albo zaufane urządzenie); `voluntary` = stan przełącznika.
 */
export const secondFactorStatusSchema = z.object({
  state: z.enum(['required', 'enabled', 'disabled']),
  reasons: z.array(z.enum(SECOND_FACTOR_REASONS)),
  voluntary: z.boolean(),
  sessionVerified: z.boolean(),
  email: z.string(),
  trustedDevices: z.array(trustedDeviceItemSchema),
});
export type SecondFactorStatus = z.infer<typeof secondFactorStatusSchema>;

export const revokeTrustedDeviceInput = z.object({ id: idSchema });
export const setVoluntarySecondFactorInput = z.object({ enabled: z.boolean() });

/** team.listMembers (Fala 0: podgląd). */
export const teamMemberSchema = z.object({
  membershipId: idSchema,
  userId: idSchema,
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  avatarColor: z.string(),
  role: roleSchema,
  status: z.enum(['active', 'disabled']),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

/** Treść błędu REST: `{ error: { code, message } }`. */
export const errorBodySchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
